import { type ClassValue, clsx } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility function to merge Tailwind CSS classes
 */
export function cn(...inputs: ClassValue[]): string {
  return twMerge(clsx(inputs));
}

/**
 * Format currency value
 */
export function formatCurrency(value: number, currency = 'BRL'): string {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency,
  }).format(value / 100);
}

/**
 * Format date
 */
export function formatDate(
  date: string | Date,
  options?: Intl.DateTimeFormatOptions
): string {
  const dateObj = typeof date === 'string' ? new Date(date) : date;
  return new Intl.DateTimeFormat('pt-BR', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
    ...options,
  }).format(dateObj);
}

/**
 * Format date and time
 */
export function formatDateTime(date: string | Date): string {
  return formatDate(date, {
    hour: '2-digit',
    minute: '2-digit',
  });
}

/**
 * Validate CPF (Brazilian tax ID)
 */
export function validateCPF(cpf: string): boolean {
  const cleanCPF = cpf.replace(/\D/g, '');

  if (cleanCPF.length !== 11) return false;

  // Check for repeated digits
  if (/^(\d)\1{10}$/.test(cleanCPF)) return false;

  // Validate first digit
  let sum = 0;
  for (let i = 0; i < 9; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (10 - i);
  }
  let remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(9))) return false;

  // Validate second digit
  sum = 0;
  for (let i = 0; i < 10; i++) {
    sum += parseInt(cleanCPF.charAt(i)) * (11 - i);
  }
  remainder = (sum * 10) % 11;
  if (remainder === 10 || remainder === 11) remainder = 0;
  if (remainder !== parseInt(cleanCPF.charAt(10))) return false;

  return true;
}

/**
 * Alias for validateCPF for backward compatibility
 */
export const isValidCPF = validateCPF;

/**
 * Format document (CPF/CNPJ) with mask
 */
export function formatDocument(document: string): string {
  const cleanDoc = document.replace(/\D/g, '');

  if (cleanDoc.length === 11) {
    // CPF format: XXX.XXX.XXX-XX
    return cleanDoc.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
  } else if (cleanDoc.length === 14) {
    // CNPJ format: XX.XXX.XXX/XXXX-XX
    return cleanDoc.replace(
      /(\d{2})(\d{3})(\d{3})(\d{4})(\d{2})/,
      '$1.$2.$3/$4-$5'
    );
  }

  return document;
}

/**
 * Format CPF during typing with progressive mask
 */
export function formatCPFAsTyping(value: string): string {
  // Remove all non-digits
  const cleanValue = value.replace(/\D/g, '');

  // Limit to 11 digits
  const limitedValue = cleanValue.slice(0, 11);

  // Apply progressive formatting
  if (limitedValue.length <= 3) {
    return limitedValue;
  } else if (limitedValue.length <= 6) {
    return limitedValue.replace(/(\d{3})(\d{0,3})/, '$1.$2');
  } else if (limitedValue.length <= 9) {
    return limitedValue.replace(/(\d{3})(\d{3})(\d{0,3})/, '$1.$2.$3');
  } else {
    return limitedValue.replace(
      /(\d{3})(\d{3})(\d{3})(\d{0,2})/,
      '$1.$2.$3-$4'
    );
  }
}

/**
 * Clean document by removing non-digits
 */
export function cleanDocument(document: string): string {
  return document.replace(/\D/g, '');
}

/**
 * Apply CPF mask
 */
export function applyCPFMask(value: string): string {
  const cleanValue = value.replace(/\D/g, '');
  return cleanValue.replace(/(\d{3})(\d{3})(\d{3})(\d{2})/, '$1.$2.$3-$4');
}

/**
 * Parse formatted amount to cents
 */
export function parseFormattedAmountToCents(amount: string): number {
  const cleanAmount = amount.replace(/[^\d,]/g, '').replace(',', '.');
  const value = parseFloat(cleanAmount);
  return Math.round(value * 100);
}

/**
 * Get contrast color for background
 */
export function getContrastColor(backgroundColor: string): string {
  // Convert hex to RGB
  const hex = backgroundColor.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);

  // Calculate luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#ffffff';
}

/**
 * Validate CNPJ (Brazilian company tax ID)
 */
export function validateCNPJ(cnpj: string): boolean {
  const cleanCNPJ = cnpj.replace(/\D/g, '');

  if (cleanCNPJ.length !== 14) return false;

  // Check for repeated digits
  if (/^(\d)\1{13}$/.test(cleanCNPJ)) return false;

  // Validate first digit
  const weights1 = [5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  let sum = 0;
  for (let i = 0; i < 12; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weights1[i];
  }
  let remainder = sum % 11;
  const digit1 = remainder < 2 ? 0 : 11 - remainder;
  if (digit1 !== parseInt(cleanCNPJ.charAt(12))) return false;

  // Validate second digit
  const weights2 = [6, 5, 4, 3, 2, 9, 8, 7, 6, 5, 4, 3, 2];
  sum = 0;
  for (let i = 0; i < 13; i++) {
    sum += parseInt(cleanCNPJ.charAt(i)) * weights2[i];
  }
  remainder = sum % 11;
  const digit2 = remainder < 2 ? 0 : 11 - remainder;
  if (digit2 !== parseInt(cleanCNPJ.charAt(13))) return false;

  return true;
}

/**
 * Validate email format
 */
export function validateEmail(email: string): boolean {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
}

/**
 * Generate random string
 */
export function generateRandomString(length = 8): string {
  const chars =
    'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
  let result = '';
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

/**
 * Debounce function
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: NodeJS.Timeout;
  return (...args: Parameters<T>) => {
    clearTimeout(timeout);
    timeout = setTimeout(() => func(...args), wait);
  };
}

/**
 * Throttle function
 */
export function throttle<T extends (...args: any[]) => any>(
  func: T,
  limit: number
): (...args: Parameters<T>) => void {
  let inThrottle: boolean;
  return (...args: Parameters<T>) => {
    if (!inThrottle) {
      func(...args);
      inThrottle = true;
      setTimeout(() => (inThrottle = false), limit);
    }
  };
}

/**
 * Deep clone object
 */
export function deepClone<T>(obj: T): T {
  if (obj === null || typeof obj !== 'object') return obj;
  if (obj instanceof Date) return new Date(obj.getTime()) as T;
  if (obj instanceof Array) return obj.map(item => deepClone(item)) as T;
  if (typeof obj === 'object') {
    const clonedObj = {} as T;
    for (const key in obj) {
      if (obj.hasOwnProperty(key)) {
        clonedObj[key] = deepClone(obj[key]);
      }
    }
    return clonedObj;
  }
  return obj;
}

/**
 * Check if object is empty
 */
export function isEmpty(obj: any): boolean {
  if (obj == null) return true;
  if (Array.isArray(obj) || typeof obj === 'string') return obj.length === 0;
  if (obj instanceof Map || obj instanceof Set) return obj.size === 0;
  if (typeof obj === 'object') return Object.keys(obj).length === 0;
  return false;
}

/**
 * Capitalize first letter
 */
export function capitalize(str: string): string {
  return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
}

/**
 * Convert string to slug
 */
export function toSlug(str: string): string {
  return str
    .toLowerCase()
    .trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '');
}

/**
 * Parse QR Code PIX data
 */
export function parsePixQRCode(qrCodeData: string): {
  amount?: number;
  name?: string;
  taxId?: string;
  city?: string;
  description?: string;
  dynamicPixUrl?: string;
  isDynamicPix?: boolean;
} {
  const result: {
    amount?: number;
    name?: string;
    taxId?: string;
    city?: string;
    description?: string;
    dynamicPixUrl?: string;
    isDynamicPix?: boolean;
  } = {};

  try {
    // Parse EMV QR Code format
    // Pattern: [2 digits tag][2 digits length][content]
    let position = 0;

    while (position < qrCodeData.length) {
      if (position + 4 > qrCodeData.length) break;

      const tag = qrCodeData.substring(position, position + 2);
      const lengthStr = qrCodeData.substring(position + 2, position + 4);
      const length = parseInt(lengthStr, 10);

      if (isNaN(length) || position + 4 + length > qrCodeData.length) break;

      const value = qrCodeData.substring(position + 4, position + 4 + length);

      switch (tag) {
        case '26': // Merchant Account Information (PIX)
          // Parse PIX data fields to extract dynamic PIX URL
          let pixPosition = 0;
          while (pixPosition < value.length) {
            if (pixPosition + 4 > value.length) break;

            const pixTag = value.substring(pixPosition, pixPosition + 2);
            const pixLengthStr = value.substring(
              pixPosition + 2,
              pixPosition + 4
            );
            const pixLength = parseInt(pixLengthStr, 10);

            if (isNaN(pixLength) || pixPosition + 4 + pixLength > value.length)
              break;

            const pixValue = value.substring(
              pixPosition + 4,
              pixPosition + 4 + pixLength
            );

            // Subtag 25 contains the dynamic PIX URL (without https://)
            if (pixTag === '25') {
              // Check if it looks like a URL (contains domain pattern)
              if (
                pixValue.includes('/pix/') ||
                pixValue.includes('.com') ||
                pixValue.includes('.com.br') ||
                pixValue.match(/^[a-z0-9.-]+\.[a-z]{2,}\//)
              ) {
                result.dynamicPixUrl = `https://${pixValue}`;
                result.isDynamicPix = true;
              }
            }

            pixPosition += 4 + pixLength;
          }
          break;
        case '54': // Amount
          const parsedAmount = parseFloat(value);
          if (!isNaN(parsedAmount) && parsedAmount > 0) {
            result.amount = parsedAmount; // Valor já está em reais
          }
          break;
        case '59': // Merchant name
          result.name = value;
          break;
        case '60': // Merchant city
          result.city = value;
          break;
        case '62': // Additional data
          // Parse additional data fields recursively
          let additionalPosition = 0;
          while (additionalPosition < value.length) {
            if (additionalPosition + 4 > value.length) break;

            const additionalTag = value.substring(
              additionalPosition,
              additionalPosition + 2
            );
            const additionalLengthStr = value.substring(
              additionalPosition + 2,
              additionalPosition + 4
            );
            const additionalLength = parseInt(additionalLengthStr, 10);

            if (
              isNaN(additionalLength) ||
              additionalPosition + 4 + additionalLength > value.length
            )
              break;

            const additionalValue = value.substring(
              additionalPosition + 4,
              additionalPosition + 4 + additionalLength
            );

            switch (additionalTag) {
              case '05': // Tax ID
                result.taxId = additionalValue;
                break;
              case '50': // Description
                result.description = additionalValue;
                break;
            }

            additionalPosition += 4 + additionalLength;
          }
          break;
      }

      position += 4 + length;
    }
  } catch (error) {
    // console.error('Error parsing PIX QR Code:', error);
  }

  return result;
}

/**
 * Decode a JWT token without verification (for reading payload only)
 */
export function decodeJwtPayload(token: string): any {
  try {
    // JWT format: header.payload.signature
    const parts = token.split('.');
    if (parts.length !== 3) {
      return null;
    }

    // Decode the payload (second part)
    const payload = parts[1];
    // Handle URL-safe base64
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );

    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Error decoding JWT:', error);
    return null;
  }
}

/**
 * Fetch amount from a dynamic PIX QR Code URL
 * The URL returns a JWT that contains the payment amount
 * Note: Most bank APIs require mTLS certificates, so this may fail in browser context
 */
export async function fetchDynamicPixAmount(
  dynamicPixUrl: string
): Promise<number | null> {
  // Create an AbortController for timeout
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

  try {
    // Fetch the JWT from the dynamic PIX URL
    const response = await fetch(dynamicPixUrl, {
      method: 'GET',
      headers: {
        Accept: 'application/jwt, application/json, text/plain, */*',
      },
      signal: controller.signal,
      mode: 'cors',
    });

    clearTimeout(timeoutId);

    if (!response.ok) {
      return null;
    }

    const jwtToken = await response.text();

    // Decode the JWT payload
    const payload = decodeJwtPayload(jwtToken.trim());

    if (!payload) {
      return null;
    }

    // Try to find the amount in common JWT fields
    // The amount might be in different fields depending on the bank
    let amount: number | null = null;

    // Check common amount field names
    if (payload.valor?.original) {
      // Itaú format: { valor: { original: "123.45" } }
      amount = parseFloat(payload.valor.original);
    } else if (payload.valor) {
      // Simple format: { valor: "123.45" } or { valor: 123.45 }
      amount =
        typeof payload.valor === 'string'
          ? parseFloat(payload.valor)
          : payload.valor;
    } else if (payload.amount) {
      // English format: { amount: 123.45 }
      amount =
        typeof payload.amount === 'string'
          ? parseFloat(payload.amount)
          : payload.amount;
    } else if (payload.value) {
      // Alternative format: { value: 123.45 }
      amount =
        typeof payload.value === 'string'
          ? parseFloat(payload.value)
          : payload.value;
    }

    // Validate the amount
    if (amount !== null && !isNaN(amount) && amount > 0) {
      return amount; // Return amount in reais (not centavos)
    }

    return null;
  } catch (error) {
    clearTimeout(timeoutId);
    // Silently fail - this is expected for bank APIs that require mTLS
    return null;
  }
}
